import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LayoutGrid, List, Plus, AlertTriangle, MoreVertical, Pencil, Trash2, Lightbulb } from 'lucide-react';
import { ProjectCard, PROJECT_STATUS_LABELS as STATUS_LABELS } from '../../components/ProjectCard';
import { useHasPermission } from '../../packages/permission-guard';
import { IdeaToProject } from '../../components/IdeaToProject';
import {
    getProjects, createProject, updateProject, deleteProject, getEntityTypes, getPortfolio,
    type Project, type EntityType, type PortfolioProject,
} from '../../packages/api-client/pm-api';
import { getErrorMessage } from '../../packages/api-client';
import { Dialog, PageHeader, StateView, Table, type Column } from '../../packages/ui';
import { formatDueDate } from '../../utils/date';
import { usePageTitle } from '../../hooks/usePageTitle';
import { hexAlpha } from '../../utils/statusColors';
import { PICKER_ICONS, EntityIcon } from '../../components/taskIcons';
import './ProjectsPage.css';
import { ProjectTypeField } from '../../components/ProjectTypeField';
import type { ProjectType } from '../../packages/api-client/pm-api';

const STATUS_OPTIONS: Array<Project['status']> = ['planning', 'active', 'on_hold', 'completed', 'archived'];

const ICONS = Object.keys(PICKER_ICONS);
const COLORS: Array<[string, string]> = [
    ['#2DE8D0', 'Aqua'], ['#14867A', 'Teal'], ['#F5A623', 'Amber'], ['#F97316', 'Orange'],
    ['#F87171', 'Red'], ['#C05CFF', 'Violet'], ['#3BA7FF', 'Blue'], ['#94A3B8', 'Slate'],
];
const PAGE_SIZE = 50;

type Load<T> = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; data: T };

interface ProjectForm {
    project_type: ProjectType;
    name: string;
    description: string;
    color: string;
    icon: string;
}
const EMPTY_FORM: ProjectForm = { project_type: 'general', name: '', description: '', color: COLORS[0][0], icon: ICONS[0] };

export function ProjectsPage() {
    usePageTitle('Projects');
    const [params, setParams] = useSearchParams();
    // Same gate as POST /projects (projects:create); the server enforces it either way.
    const canCreate = useHasPermission('projects', 'create');
    const view = params.get('view') === 'portfolio' ? 'portfolio' : 'cards';
    const q = params.get('q') ?? '';
    const status = params.get('status') ?? '';

    // View changes push a history entry (Back returns to the previous view);
    // typing in filters replaces it.
    const setParam = (key: string, value: string, push = false) => {
        setParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value); else next.delete(key);
            return next;
        }, { replace: !push });
    };
    const clearFilters = () => setParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('q'); next.delete('status');
        return next;
    }, { replace: true });

    // ── Projects (paginated, server total) ──
    const [list, setList] = useState<Load<{ items: Project[]; total: number; page: number }>>({ state: 'loading' });
    const [loadingMore, setLoadingMore] = useState(false);
    const [moreError, setMoreError] = useState('');

    const loadFirst = useCallback(() => {
        setList({ state: 'loading' });
        getProjects(1, PAGE_SIZE)
            .then(r => setList({ state: 'ready', data: { items: r.data ?? [], total: r.meta?.total ?? (r.data ?? []).length, page: 1 } }))
            .catch(e => setList({ state: 'error', message: getErrorMessage(e) }));
    }, []);
    useEffect(loadFirst, [loadFirst]);

    async function loadMore() {
        if (list.state !== 'ready') return;
        setLoadingMore(true); setMoreError('');
        try {
            const r = await getProjects(list.data.page + 1, PAGE_SIZE);
            setList(prev => prev.state === 'ready'
                ? { state: 'ready', data: { items: mergeById(prev.data.items, r.data ?? []), total: r.meta?.total ?? prev.data.total, page: prev.data.page + 1 } }
                : prev);
        } catch (e) {
            setMoreError(getErrorMessage(e));
        } finally { setLoadingMore(false); }
    }

    // After a create/delete, offsets shift server-side: reload page 1 rather
    // than patching the list locally, so Load more never skips a row.
    const reloadAll = () => { setPortfolio(null); loadFirst(); };

    // ── Portfolio: one unpaginated, viewer-scoped response. It feeds the
    // Portfolio table and each card's progress and health; numbers are the API's. ──
    const [portfolio, setPortfolio] = useState<Load<PortfolioProject[]> | null>(null);
    const loadPortfolio = useCallback(() => {
        setPortfolio({ state: 'loading' });
        getPortfolio()
            .then(data => setPortfolio({ state: 'ready', data }))
            .catch(e => setPortfolio({ state: 'error', message: getErrorMessage(e) }));
    }, []);
    useEffect(() => {
        if (portfolio === null) loadPortfolio();
    }, [portfolio, loadPortfolio]);
    const stats = new Map(portfolio?.state === 'ready' ? portfolio.data.map(p => [p.id, p]) : []);

    // ── Create / edit / delete ──
    const [createOpen, setCreateOpen] = useState(false);
    const [ideaOpen, setIdeaOpen] = useState(false);
    const [editing, setEditing] = useState<Project | null>(null);
    const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
    const [actionError, setActionError] = useState('');

    async function handleDelete(project: Project) {
        setMenuOpenId(null);
        if (!window.confirm(`Delete project "${project.name}"? This cannot be undone.`)) return;
        setActionError('');
        try {
            await deleteProject(project.id);
            reloadAll();
        } catch (e) {
            setActionError(`Couldn't delete "${project.name}": ${getErrorMessage(e)}`);
        }
    }

    const matches = (name: string, description: string, s: string) =>
        (!status || s === status) &&
        (!q || name.toLowerCase().includes(q.toLowerCase()) || description.toLowerCase().includes(q.toLowerCase()));
    const filtersActive = Boolean(q || status);

    const count = list.state === 'ready'
        ? (list.data.items.length < list.data.total
            ? `Showing ${list.data.items.length} of ${list.data.total} projects`
            : `${list.data.total} project${list.data.total !== 1 ? 's' : ''}`)
        : undefined;

    return (
        <div className="projects-page">
            <PageHeader
                eyebrow="Workspace / Projects"
                title="A home for every project."
                subtitle={<>Each project keeps its own progress, plan, tasks, people, and access.
                    {count && <span className="projects-count">{count}</span>}</>}
                actions={canCreate && <>
                    <button type="button" className="btn btn-secondary btn-md projects-btn" onClick={() => setIdeaOpen(true)}>
                        <Lightbulb size={16} aria-hidden="true" /> Start from an idea
                    </button>
                    <button type="button" className="btn btn-primary btn-md projects-btn" onClick={() => setCreateOpen(true)}>
                        <Plus size={16} aria-hidden="true" /> New project
                    </button>
                </>}
            />

            <div className="projects-toolbar">
                <div className="projects-view-toggle" role="group" aria-label="View">
                    <button type="button" aria-pressed={view === 'cards'} onClick={() => view !== 'cards' && setParam('view', '', true)}>
                        <LayoutGrid size={16} aria-hidden="true" /> Cards
                    </button>
                    <button type="button" aria-pressed={view === 'portfolio'} onClick={() => view !== 'portfolio' && setParam('view', 'portfolio', true)}>
                        <List size={16} aria-hidden="true" /> Portfolio
                    </button>
                </div>
                <label className="projects-filter">
                    <span className="sr-only">Search projects</span>
                    <input type="search" className="form-input" placeholder="Search projects…"
                        value={q} onChange={e => setParam('q', e.target.value)} />
                </label>
                <label className="projects-filter">
                    <span className="form-label">Status</span>
                    <select className="form-input" value={status} onChange={e => setParam('status', e.target.value)}>
                        <option value="">All statuses</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                </label>
            </div>

            {actionError && <div className="projects-notice" role="alert">{actionError}</div>}

            {view === 'portfolio' ? (
                <PortfolioView load={portfolio} onRetry={loadPortfolio}
                    rows={portfolio?.state === 'ready' ? portfolio.data.filter(p => matches(p.name, p.description ?? '', p.status)) : []}
                    filtersActive={filtersActive} onClearFilters={clearFilters} />
            ) : list.state === 'loading' ? (
                <StateView state="loading" label="Loading projects">
                    <div className="projects-grid">
                        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton projects-card-skeleton" aria-hidden="true" />)}
                    </div>
                </StateView>
            ) : list.state === 'error' ? (
                <StateView state="error" title="Couldn't load projects" message={list.message} onRetry={loadFirst} />
            ) : list.data.items.length === 0 ? (
                <StateView state="empty" title="No projects yet"
                    message="Create a project to start planning work, or start from an idea." />
            ) : (() => {
                const shown = list.data.items.filter(p => matches(p.name, p.description ?? '', p.status));
                const partial = list.data.items.length < list.data.total;
                return <>
                    {partial && filtersActive && (
                        <p className="projects-muted projects-hint">Only loaded projects are searched; load more to search the rest.</p>
                    )}
                    {shown.length === 0 ? (
                        <StateView state="filtered-empty" title="No projects match these filters"
                            onClearFilters={clearFilters} />
                    ) : (
                        <div className="pcard-grid" role="list" aria-label="Projects">
                            {shown.map(project => (
                                <ProjectsPageCard key={project.id} project={project} stats={stats.get(project.id)}
                                    menuOpen={menuOpenId === project.id}
                                    onToggleMenu={open => setMenuOpenId(open ? project.id : null)}
                                    onEdit={() => { setMenuOpenId(null); setEditing(project); }}
                                    onDelete={() => handleDelete(project)} />
                            ))}
                        </div>
                    )}
                    {partial && (
                        <div className="projects-more">
                            {moreError && <span role="alert">Couldn't load more: {moreError}</span>}
                            <button type="button" className="btn btn-secondary btn-md" onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? 'Loading…' : 'Load more projects'}
                            </button>
                        </div>
                    )}
                </>;
            })()}

            {createOpen && (
                <CreateProjectDialog
                    onClose={() => setCreateOpen(false)}
                    onCreated={() => { setCreateOpen(false); reloadAll(); }}
                    onStartFromIdea={() => { setCreateOpen(false); setIdeaOpen(true); }} />
            )}
            {editing && (
                <EditProjectDialog project={editing}
                    onClose={() => setEditing(null)}
                    onSaved={p => {
                        setList(prev => prev.state === 'ready'
                            ? { state: 'ready', data: { ...prev.data, items: prev.data.items.map(x => x.id === p.id ? { ...x, ...p } : x) } }
                            : prev);
                        setPortfolio(null); setEditing(null);
                    }} />
            )}
            {ideaOpen && <IdeaToProject onClose={() => setIdeaOpen(false)} />}
        </div>
    );
}

function mergeById(a: Project[], b: Project[]): Project[] {
    const seen = new Set(a.map(p => p.id));
    return [...a, ...b.filter(p => !seen.has(p.id))];
}

// ─── Cards ────────────────────────────────────────────────────────────────────

/** The shared card plus this page's Edit/Delete options menu. */
function ProjectsPageCard({ project, stats, menuOpen, onToggleMenu, onEdit, onDelete }: {
    project: Project; stats?: PortfolioProject; menuOpen: boolean; onToggleMenu: (open: boolean) => void; onEdit: () => void; onDelete: () => void;
}) {
    const menuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!menuOpen) return;
        const onDown = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) onToggleMenu(false); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onToggleMenu(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [menuOpen, onToggleMenu]);

    return (
        <div role="listitem" className="projects-card-item">
            <ProjectCard project={project} stats={stats} actions={
                <div ref={menuRef} className="projects-card-menu">
                    <button type="button" className="btn btn-ghost btn-sm project-card-menu-btn"
                        aria-label={`Options for ${project.name}`} aria-haspopup="menu" aria-expanded={menuOpen}
                        onClick={() => onToggleMenu(!menuOpen)}>
                        <MoreVertical size={16} aria-hidden="true" />
                    </button>
                    {menuOpen && (
                        <div role="menu" className="projects-menu">
                            <button type="button" role="menuitem" onClick={onEdit}><Pencil size={14} aria-hidden="true" /> Edit or archive</button>
                            <button type="button" role="menuitem" className="projects-menu-danger" onClick={onDelete}><Trash2 size={14} aria-hidden="true" /> Delete</button>
                        </div>
                    )}
                </div>
            } />
        </div>
    );
}

// ─── Portfolio ────────────────────────────────────────────────────────────────

type PortfolioRow = PortfolioProject & Record<string, unknown>;

const PORTFOLIO_COLUMNS: Column<PortfolioRow>[] = [
    {
        key: 'name', header: 'Project', rowHeader: true, render: p => (
            <span className="projects-inline">
                <span className="projects-portfolio-icon" style={{ background: hexAlpha(p.color || '#14867A', 0.13) }}><EntityIcon icon={p.icon} size={16} /></span>
                <Link to={`/projects/${p.id}`}>{p.name}</Link>
            </span>
        ),
    },
    { key: 'status', header: 'Status', render: p => <span className={`status-badge status-${p.status}`}>{STATUS_LABELS[p.status] ?? p.status}</span> },
    {
        key: 'percent', header: 'Progress', render: p => (
            <span className="projects-progress">
                <span className="projects-progress-track" aria-hidden="true"><span style={{ width: `${p.percent}%`, background: p.color || 'var(--color-primary)' }} /></span>
                <span>{Math.round(p.percent)}%</span>
            </span>
        ),
    },
    { key: 'tasks', header: 'Done / tasks', render: p => `${p.done_tasks}/${p.total_tasks}` },
    {
        key: 'overdue', header: 'Overdue', render: p => p.overdue_tasks > 0
            ? <span className="projects-inline projects-overdue"><AlertTriangle size={14} aria-hidden="true" />{p.overdue_tasks}</span>
            : <span aria-label="None">—</span>,
    },
    { key: 'members', header: 'Members', render: p => p.member_count },
    { key: 'due', header: 'Due date', render: p => p.due_date ? formatDueDate(p.due_date) : '—' },
];

function PortfolioView({ load, rows, onRetry, filtersActive, onClearFilters }: {
    load: Load<PortfolioProject[]> | null; rows: PortfolioProject[]; onRetry: () => void;
    filtersActive: boolean; onClearFilters: () => void;
}) {
    if (!load || load.state === 'loading') return <StateView state="loading" label="Loading portfolio" lines={5} />;
    if (load.state === 'error') return <StateView state="error" title="Couldn't load the portfolio" message={load.message} onRetry={onRetry} />;
    if (load.data.length === 0) return <StateView state="empty" title="No projects in the portfolio" message="Projects you can see appear here with their progress." />;
    if (rows.length === 0 && filtersActive) return <StateView state="filtered-empty" onClearFilters={onClearFilters} />;
    return (
        <Table<PortfolioRow> caption="Project portfolio" hideCaption columns={PORTFOLIO_COLUMNS}
            data={rows as PortfolioRow[]} rowKey={p => p.id} />
    );
}

// ─── Dialogs ──────────────────────────────────────────────────────────────────

function ProjectFields({ form, setForm, idPrefix }: {
    form: ProjectForm; setForm: React.Dispatch<React.SetStateAction<ProjectForm>>; idPrefix: string;
}) {
    return <>
        <ProjectTypeField id={`${idPrefix}-type`} value={form.project_type} onChange={project_type => setForm(f => ({ ...f, project_type }))} />
        <div className="form-group">
            <label className="form-label" htmlFor={`${idPrefix}-name`}>Project name (required)</label>
            <input id={`${idPrefix}-name`} className="form-input" required autoFocus
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        </div>
        <div className="form-group">
            <label className="form-label" htmlFor={`${idPrefix}-desc`}>Description</label>
            <input id={`${idPrefix}-desc`} className="form-input"
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <fieldset className="form-group projects-fieldset">
            <legend className="form-label">Icon</legend>
            <div className="projects-swatches">
                {ICONS.map(icon => (
                    <button key={icon} type="button" className="projects-icon-choice" aria-label={icon} aria-pressed={form.icon === icon}
                        onClick={() => setForm(f => ({ ...f, icon }))}>
                        <EntityIcon icon={icon} size={18} />
                    </button>
                ))}
            </div>
        </fieldset>
        <fieldset className="form-group projects-fieldset">
            <legend className="form-label">Color</legend>
            <div className="projects-swatches">
                {COLORS.map(([color, label]) => (
                    <button key={color} type="button" className="projects-color-choice" aria-label={label} aria-pressed={form.color === color}
                        style={{ background: color }} onClick={() => setForm(f => ({ ...f, color }))} />
                ))}
            </div>
        </fieldset>
    </>;
}

function CreateProjectDialog({ onClose, onCreated, onStartFromIdea }: {
    onClose: () => void; onCreated: (p: Project) => void; onStartFromIdea: () => void;
}) {
    const [form, setForm] = useState<ProjectForm>(EMPTY_FORM);
    const [entityTypeId, setEntityTypeId] = useState('');
    const [types, setTypes] = useState<Load<EntityType[]>>({ state: 'loading' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        getEntityTypes()
            .then(data => setTypes({ state: 'ready', data }))
            .catch(e => setTypes({ state: 'error', message: getErrorMessage(e) }));
    }, []);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true); setError('');
        try {
            const payload: Partial<Project> = { ...form };
            if (entityTypeId) payload.entity_type_id = entityTypeId;
            onCreated(await createProject(payload));
        } catch (err) {
            setError(getErrorMessage(err));
        } finally { setSaving(false); }
    }

    return (
        <Dialog open onClose={onClose} title="New project" closeOnBackdrop={false}>
            <form onSubmit={submit} className="projects-form">
                <ProjectFields form={form} setForm={setForm} idPrefix="new-project" />
                <fieldset className="form-group projects-fieldset">
                    <legend className="form-label">Record type (optional)</legend>
                    {types.state === 'loading' ? <span className="projects-muted">Loading types…</span>
                        : types.state === 'error' ? <span className="projects-muted" role="alert">Couldn't load record types; the project can be created without one.</span>
                        : types.data.length === 0 ? <span className="projects-muted">No record types defined.</span>
                        : (
                            <div className="projects-swatches">
                                {types.data.map(et => (
                                    <button key={et.id} type="button" className="projects-type-choice" aria-pressed={entityTypeId === et.id}
                                        onClick={() => setEntityTypeId(id => id === et.id ? '' : et.id)}>
                                        <EntityIcon icon={et.icon} size={14} /> {et.name}
                                    </button>
                                ))}
                            </div>
                        )}
                </fieldset>
                {error && <div className="projects-form-error" role="alert">{error}</div>}
                <div className="projects-form-actions">
                    <button type="button" className="btn btn-ghost btn-md projects-btn projects-form-alt" onClick={onStartFromIdea}>
                        <Lightbulb size={16} aria-hidden="true" /> Start from an idea instead
                    </button>
                    <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-md" disabled={saving}>{saving ? 'Creating…' : 'Create project'}</button>
                </div>
            </form>
        </Dialog>
    );
}

function EditProjectDialog({ project, onClose, onSaved }: {
    project: Project; onClose: () => void; onSaved: (p: Project) => void;
}) {
    const [form, setForm] = useState<ProjectForm>({
        project_type: project.project_type ?? 'software',
        name: project.name, description: project.description ?? '',
        color: project.color || COLORS[0][0], icon: project.icon || ICONS[0],
    });
    const [status, setStatus] = useState<Project['status']>(project.status);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true); setError('');
        try {
            onSaved(await updateProject(project.id, { ...form, status }));
        } catch (err) {
            setError(getErrorMessage(err));
        } finally { setSaving(false); }
    }

    return (
        <Dialog open onClose={onClose} title="Edit project" closeOnBackdrop={false}>
            <form onSubmit={submit} className="projects-form">
                <ProjectFields form={form} setForm={setForm} idPrefix="edit-project" />
                <div className="form-group">
                    <label className="form-label" htmlFor="edit-project-status">Status</label>
                    <select id="edit-project-status" className="form-input" value={status}
                        onChange={e => setStatus(e.target.value as Project['status'])}>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                    <span className="projects-muted">Archive a project by setting its status to Archived.</span>
                </div>
                {error && <div className="projects-form-error" role="alert">{error}</div>}
                <div className="projects-form-actions">
                    <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-md" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                </div>
            </form>
        </Dialog>
    );
}
